const { getThresholds } = require('../../../forecast/tools/read/psm-thresholds')
const { DAYS_PER_MONTH } = require('../../constants')
const { selectCostEffectiveSku } = require('./select-cost-effective-sku')

/**
 * Assembles the normalised input array consumed by runBudgetOptimization.
 * Joins warehouseStock, ledger, qtoData and program thresholds into a flat
 * per-product array ready for urgency scoring and allocation.
 *
 *
 * @param {object} params
 * @param {{
 *   genericGroups: Array<{
 *     genericId: string|number,
 *     products: Array<{
 *       productId: string,
 *       genericFactor: number,
 *       sellPrice: number
 *     }>
 *   }>
 * }} params.warehouseStock
 *   Result of api.order.listWarehouseStock(). genericGroups groups all
 *   pack-size variants of the same generic product together.
 *
 * @param {Object<string, number>} params.ledger
 *
 *
 * @param {{
 *   products: Object<string, {
 *     soh: number,
 *     consumed: number|null,
 *     original: number
 *   }>
 * }|null} params.qtoData
 *
 * @param {string} params.programId
 *
 *
 * @param {number} params.monthsInPeriod
 *
 *
 */
function buildAlgorithmInput ({ warehouseStock, ledger, qtoData, programId, monthsInPeriod }) {
  const thresholds = getThresholds(programId)

  if (!thresholds) {
    const err = new Error(`No stock thresholds found for program: ${programId}`)
    throw err
  }

  const { min, reOrder, max } = thresholds

  // Lead time = gap between re-order point and minimum safety stock level.
  // Derived from thresholds because both encode the same supply plan policy.
  // PRD: QTO = (target_months + lead_time) × AMC − SOH.
  const leadTimeMonths = reOrder.months - min.months

  const { genericGroups = [] } = warehouseStock
  // qtoData.products is keyed by genericId. Default to empty object so every
  // group lookup below safely returns undefined (→ skip) rather than throwing.
  const qtoProducts = (qtoData && qtoData.products) || {}

  const inputs = []
  let totalAmc = 0
  let maxCostPerUnit = 0

  for (const group of genericGroups) {
    // ── Step 1: Enrich every SKU with its warehouse availability ──────────────
    const productsWithAvailability = group.products.map(p => {
      const availablePacks = ledger ? ledger[p.productId] : undefined
      return {
        ...p,
        warehouseAvailablePacks: (availablePacks != null && isFinite(availablePacks))
          ? availablePacks
          : Infinity
      }
    })

    // ── Step 2: Prefer SKUs with actual stock ─────────────────────────────────
    const availableProducts = productsWithAvailability.filter(p => p.warehouseAvailablePacks > 0)
    const candidatePool = availableProducts.length > 0 ? availableProducts : productsWithAvailability

    // ── Step 3: Pick the cheapest-per-unit SKU ────────────────────────────────
    const bestSku = selectCostEffectiveSku(candidatePool)
    if (!bestSku) continue

    // ── Step 4: Join to QTO data
    // QTO data is keyed by genericId, not by SKU productId.
    const qto = qtoProducts[group.genericId]
    // No QTO entry = no stock report at this facility for this product.
    // Without consumption history we cannot compute AMC or set targets.
    if (!qto) continue

    // Zero soh + zero consumed = no basis for a suggestion, so skip it (otherwise the
    // budget allocator would spend on a product we won't recommend).
    if (!qto.soh && !qto.consumed) continue

    // ── Step 5: Derive AMC
    // Prefer per-product dailyDemand from forecast; fall back to consumed / period.
    const hasForecastLevels = qto.maxLevel != null
    let amc
    if (hasForecastLevels && qto.dailyDemand != null) {
      amc = qto.dailyDemand * DAYS_PER_MONTH
    } else {
      if (qto.consumed == null) continue
      amc = qto.consumed / monthsInPeriod
    }
    // Products with zero consumption have no meaningful targets — skip them.
    if (amc === 0) continue

    const soh = qto.soh != null ? qto.soh : 0

    // ── Step 6: Compute effective stock targets (with lead time) ──────────────
    // Per-product forecast levels (units) take precedence over program thresholds × AMC.
    const reOrderTarget = hasForecastLevels ? qto.reorderPoint : amc * reOrder.months
    const effectiveMinTarget = hasForecastLevels ? qto.minLevel : amc * (min.months + leadTimeMonths)
    const effectiveMaxTarget = hasForecastLevels ? qto.maxLevel : amc * (max.months + leadTimeMonths)

    // ── Step 7: Tag whether product is in deficit or healthy range
    const isBelowReOrder = soh < reOrderTarget

    // Exclude unavailable products — they won't be allocated and must not
    // distort the velocity/cost baselines used for urgency scoring.
    if (!isFinite(bestSku.warehouseAvailablePacks)) continue

    // Checks are scoped to below-reOrder products
    // Healthy products are satisfied after products below reOrder are satisfied
    // and don't influence the velocity/cost baselines for urgency scoring.
    if (isBelowReOrder) {
      totalAmc += amc
      const costPerUnit = bestSku.sellPrice / bestSku.genericFactor
      if (costPerUnit > maxCostPerUnit) maxCostPerUnit = costPerUnit
    }

    inputs.push({
      productId: bestSku.productId,
      genericId: group.genericId,
      genericFactor: bestSku.genericFactor,
      sellPrice: bestSku.sellPrice,
      soh,
      amc,
      minMonths: min.months,
      maxMonths: max.months,
      leadTimeMonths,
      effectiveMinTarget,
      effectiveMaxTarget,
      suggestedQto: qto.original != null ? qto.original : 0,
      warehouseAvailablePacks: bestSku.warehouseAvailablePacks,
      isBelowReOrder
    })
  }

  const belowReOrderCount = inputs.filter(i => i.isBelowReOrder).length
  const avgAmc = belowReOrderCount > 0 ? totalAmc / belowReOrderCount : 0

  return { inputs, avgAmc, maxCostPerUnit }
}

exports.buildAlgorithmInput = buildAlgorithmInput
